// @ts-nocheck

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../client';

import KLBannerButton from './common/KLBannerButton';

const Wrapper = styled.div`
  position: relative;

  ${!Target.__MOBILE__
    ? css`
        transition: filter 200ms ease-in-out;

        &:hover {
          filter: brightness(1.1);
        }
      `
    : undefined};

  ${({ onClick }) => {
    if (onClick) {
      return css`
        cursor: pointer;
      `;
    }
  }}

  ${({ $show }) => css`
    opacity: ${$show ? 1 : 0};
  `}
`;
const sharedImgStyle = css`
  border-radius: 0.6em;
  width: 100%;
  padding: 0;
  margin: 0;
`;

const visibleImgStyle = css`
  position: absolute;
  left: 0;
  top: 0;
`;

const StaticBackground = styled.img`
  ${sharedImgStyle}
  ${visibleImgStyle}
`;

const AnimatedBackground = styled.img`
  ${sharedImgStyle}
  ${visibleImgStyle}
`;

const InvisibleBackground = styled.img`
  ${sharedImgStyle}
  opacity: 0;
`;

const showContentAnim = keyframes`
  50% {
    transform: scale(1.1);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  position: absolute;
  right: 0;
  top: 0;

  padding: 0.4em 0.6em;
  transition: opacity 300ms ease-in-out;

  ${({ $show }) =>
    $show
      ? css`
          opacity: 1;
          animation: ${showContentAnim} 300ms ease-in-out 1 300ms;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `}
`;

const Text = styled.div`
  color: #fff;
  font-family: 'BettingKalam';
  font-weight: bold;
  font-size: 0.8em;
  text-transform: uppercase;
  text-shadow:
    0 0 0.6em #000,
    0 0 0.6em #000,
    0 0 0.6em #000,
    0 0 0.6em #000;
  text-align: right;
  margin-bottom: 0.1em;
`;

type KLBannerProps = {
  className?: string;
  backgroundPath: string;
  animatedBackgroundPath: string | null;
  text: string;
  buttonLabel?: string;
  buttonAction?: () => void;
  onClick?: () => void;
};

const KLBanner = ({
  className,
  backgroundPath,
  animatedBackgroundPath,
  text,
  buttonLabel,
  buttonAction,
  onClick,
}: KLBannerProps) => {
  const wrapperRef = useRef();
  const invisibleImgRef = useRef();

  const [animateBackground, setAnimateBackground] = useState(!!animatedBackgroundPath);
  const backgroundPathRef = useRef(backgroundPath);
  const animateTimeoutIdRef = useRef(null);
  const [imgHasLoaded, setImgHasLoaded] = useState(false);

  useEffect(() => {
    backgroundPathRef.current = backgroundPath;
  }, [backgroundPath]);

  useEffect(() => {
    setImgHasLoaded(false);
    clearTimeout(animateTimeoutIdRef.current);
    animateTimeoutIdRef.current = null;
  }, [backgroundPath, animatedBackgroundPath]);

  useEffect(() => {
    setAnimateBackground(!!animatedBackgroundPath);
  }, [animatedBackgroundPath]);

  const onAnimatedBackgroundLoaded = useCallback((e) => {
    if (e.target.complete) {
      setImgHasLoaded(true);
      const wrapperNode = wrapperRef.current;
      const invisibleImgNode = invisibleImgRef.current;

      if (wrapperNode && invisibleImgNode) {
        wrapperNode.style.minHeight = window.getComputedStyle(invisibleImgNode)?.height ?? '0px';
      }

      animateTimeoutIdRef.current = setTimeout(() => {
        if (backgroundPathRef.current) {
          setAnimateBackground(false);
        }
      }, 2700);
    }
  }, []);

  useEffect(() => {
    return () => {
      const animateTimeoutId = animateTimeoutIdRef.current;

      if (animateTimeoutId) {
        clearTimeout(animateTimeoutId);
      }
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef} className={className} onClick={onClick} $show={imgHasLoaded}>
      <InvisibleBackground
        ref={invisibleImgRef}
        onLoad={onAnimatedBackgroundLoaded}
        src={animatedBackgroundPath ?? backgroundPath}
      />
      {animateBackground ? <AnimatedBackground src={animatedBackgroundPath} /> : null}
      {!animateBackground ? <StaticBackground src={backgroundPath} /> : null}
      <Content $show={imgHasLoaded && !animateBackground}>
        {text ? <Text>{text}</Text> : null}
        {buttonLabel ? <KLBannerButton onClick={buttonAction}>{buttonLabel}</KLBannerButton> : null}
      </Content>
    </Wrapper>
  );
};

export default KLBanner;
